import { formatDateInZone } from "@nurion/datetime/format-date-in-zone";

import type { RoomSummary } from "~/lib/matrix";
import { LOCAL_ZONE } from "~/lib/relative-time";
import { recordSdkBreadcrumb } from "~/lib/telemetry";

const FULL_DATE_TIME: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
};

export type SlashCommandArg = "text" | "duration" | "user" | "query";

export interface SlashCtx {
  input: string;
  argsText: string;
  sendAgentCommand: (agentUserId: string, name: string, args: string) => Promise<void>;
  sendText: (body: string) => Promise<void>;
  sendEmote: (body: string) => Promise<void>;
  setAway: () => Promise<void>;
  openDm: (userId: string) => Promise<void>;
  inviteUser: (userId: string) => Promise<void>;
  openSearch: (query: string) => void;
  showToast: (message: string) => void;
  findDirectRoom: (userId: string) => RoomSummary | null;
  setDndUntil: (until: Date | null) => void;
  setDndOff: () => void;
  scheduleReminder: (sendAt: number, body: string) => Promise<void>;
}

export type SlashCommand = {
  name: string;
  description: string;
  usage: string;
  args: SlashCommandArg[];
  handler: (ctx: SlashCtx) => Promise<void>;
  agent?: { userId: string; displayName: string };
};

const INVITE_TARGET = /^@[^\s:]+:\S+$/;

const commands = new Map<string, SlashCommand>();

export interface AgentCommandContent {
  [key: string]: unknown;
  msgtype: "m.text";
  body: string;
  "m.mentions": { user_ids: [string] };
  "com.nurion.command": { version: 1; agent: string; name: string; args: string };
}

export function agentCommandContent(
  agentUserId: string,
  name: string,
  args: string,
): AgentCommandContent {
  return {
    msgtype: "m.text",
    body: args ? `/${name} ${args}` : `/${name}`,
    "m.mentions": { user_ids: [agentUserId] },
    "com.nurion.command": { version: 1, agent: agentUserId, name, args },
  };
}

export function register(cmd: SlashCommand): void {
  commands.set(cmd.name, cmd);
}

export function list(): SlashCommand[] {
  return Array.from(commands.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function match(input: string): SlashCommand | null {
  const name = commandName(input);
  if (!name) return null;
  return commands.get(name) ?? null;
}

export function matchWith(
  input: string,
  extra: readonly SlashCommand[],
): { builtin: SlashCommand | null; agents: SlashCommand[] } {
  const name = commandName(input);
  if (!name) return { builtin: null, agents: [] };
  const builtin = commands.get(name) ?? null;
  if (builtin) return { builtin, agents: [] };
  const agents = extra.filter((cmd) => cmd.name.toLowerCase() === name);
  return { builtin: null, agents };
}

export function commandName(input: string): string | null {
  const trimmed = input.trimStart();
  if (!trimmed.startsWith("/")) return null;
  const token = trimmed.slice(1).split(/\s+/, 1)[0]?.toLowerCase() ?? "";
  return token.length > 0 ? token : null;
}

export function argsText(input: string): string {
  const trimmed = input.trimStart();
  if (!trimmed.startsWith("/")) return "";
  const firstSpace = trimmed.search(/\s/);
  return firstSpace === -1 ? "" : trimmed.slice(firstSpace).trim();
}

function requireArgs(ctx: SlashCtx, cmd: SlashCommand): string | null {
  const text = ctx.argsText.trim();
  if (text.length > 0) return text;
  ctx.showToast(`Use ${cmd.usage}`);
  return null;
}

function parseUserId(raw: string): string | null {
  const text = raw.trim();
  if (text.length === 0) return null;
  const token = text.split(/\s+/, 1)[0] ?? "";
  if (token.startsWith("@") && token.includes(":")) return token;
  if (token.startsWith("@")) return `${token}:nurion`;
  return null;
}

function recordSlash(command: string, success: boolean): void {
  recordSdkBreadcrumb("matrix.slash", "executed slash command", null, { command, success });
}

function durationMs(raw: string): number | null {
  const match = /^(\d+)([mhd])$/.exec(raw.trim());
  if (!match) return null;
  const amount = Number(match[1]);
  if (!Number.isSafeInteger(amount) || amount <= 0) return null;
  const unit = match[2];
  if (unit === "m") return amount * 60_000;
  if (unit === "h") return amount * 60 * 60_000;
  return amount * 24 * 60 * 60_000;
}

function parseClock(raw: string, base: Date, offsetDays: number): number | null {
  const match = /^(\d{1,2})(?::(\d{2}))?(am|pm)?$/i.exec(raw.trim());
  if (!match) return null;
  const suffix = match[3]?.toLowerCase();
  let hour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;
  if (suffix) {
    if (hour < 1 || hour > 12) return null;
    if (suffix === "am") hour = hour === 12 ? 0 : hour;
    if (suffix === "pm") hour = hour === 12 ? 12 : hour + 12;
  } else if (hour < 0 || hour > 23) {
    return null;
  }
  const next = new Date(base);
  next.setDate(base.getDate() + offsetDays);
  next.setHours(hour, minute, 0, 0);
  return next.getTime();
}

function parseReminder(
  args: string,
  nowMs: number = Date.now(),
): { sendAt: number; body: string } | null {
  const text = args.trim();
  let sendAt: number | null = null;
  let body = "";
  const relative = /^in\s+(\S+)\s+([\s\S]+)$/.exec(text);
  const calendar = /^(today|tomorrow)\s+(\S+)\s+([\s\S]+)$/.exec(text);
  const absolute = /^(\S+)\s+([\s\S]+)$/.exec(text);
  if (relative) {
    const ms = durationMs(relative[1] ?? "");
    if (ms === null) return null;
    sendAt = nowMs + ms;
    body = relative[2] ?? "";
  } else if (calendar) {
    const day = calendar[1];
    sendAt = parseClock(calendar[2] ?? "", new Date(nowMs), day === "tomorrow" ? 1 : 0);
    body = calendar[3] ?? "";
  } else if (absolute) {
    const token = absolute[1] ?? "";
    if (
      !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{3})?)?(?:Z|[+-]\d{2}:\d{2})?$/.test(token)
    ) {
      return null;
    }
    const parsed = Date.parse(token);
    if (!Number.isFinite(parsed)) return null;
    sendAt = parsed;
    body = absolute[2] ?? "";
  }
  const trimmedBody = body.trim();
  if (sendAt === null || sendAt <= nowMs || trimmedBody.length === 0) return null;
  return { sendAt, body: trimmedBody };
}

const builtIns: SlashCommand[] = [
  {
    name: "away",
    description: "Set your presence to away.",
    usage: "/away",
    args: [],
    handler: async (ctx) => {
      await ctx.setAway();
    },
  },
  {
    name: "dm",
    description: "Open or create a direct message.",
    usage: "/dm <user>",
    args: ["user"],
    handler: async (ctx) => {
      const userId = parseUserId(ctx.argsText);
      if (!userId) {
        ctx.showToast("Use /dm @alice");
        return;
      }
      await ctx.openDm(userId);
    },
  },
  {
    name: "dnd",
    description: "Set do not disturb.",
    usage: "/dnd [duration]",
    args: ["duration"],
    handler: async (ctx) => {
      const text = ctx.argsText.trim().toLowerCase();
      if (text === "off" || text === "0") {
        ctx.setDndOff();
        ctx.showToast("Do Not Disturb is off");
        recordSlash("dnd", true);
        return;
      }
      const ms = durationMs(text);
      if (ms === null) {
        ctx.showToast("Use /dnd 30m, /dnd 2h, /dnd 1d, or /dnd off.");
        recordSlash("dnd", false);
        return;
      }
      const until = new Date(Date.now() + ms);
      ctx.setDndUntil(until);
      ctx.showToast(
        `Do Not Disturb until ${formatDateInZone(until.toISOString(), LOCAL_ZONE, FULL_DATE_TIME)}`,
      );
      recordSlash("dnd", true);
    },
  },
  {
    name: "me",
    description: "Send an action message.",
    usage: "/me <action>",
    args: ["text"],
    handler: async (ctx) => {
      const text = requireArgs(ctx, builtIns[3]!);
      if (!text) return;
      await ctx.sendEmote(text);
    },
  },
  {
    name: "remind",
    description: "Schedule a reminder.",
    usage: "/remind <time> <message>",
    args: ["duration", "text"],
    handler: async (ctx) => {
      const reminder = parseReminder(ctx.argsText);
      if (!reminder) {
        ctx.showToast(
          "Use /remind in 30m text, /remind today 14:30 text, /remind tomorrow 9am text, or /remind 2026-05-15T09:00 text.",
        );
        recordSlash("remind", false);
        return;
      }
      try {
        await ctx.scheduleReminder(reminder.sendAt, reminder.body);
      } catch (err) {
        console.error("schedule reminder failed", err);
        ctx.showToast("Couldn't schedule reminder. Try again.");
        recordSlash("remind", false);
        return;
      }
      ctx.showToast(
        `Reminder scheduled for ${formatDateInZone(
          new Date(reminder.sendAt).toISOString(),
          LOCAL_ZONE,
          FULL_DATE_TIME,
        )}`,
      );
      recordSlash("remind", true);
    },
  },
  {
    name: "search",
    description: "Open message search.",
    usage: "/search <query>",
    args: ["query"],
    handler: async (ctx) => {
      const query = requireArgs(ctx, builtIns[5]!);
      if (!query) return;
      ctx.openSearch(query);
    },
  },
  {
    name: "shrug",
    description: "Send text with a shrug.",
    usage: "/shrug [text]",
    args: ["text"],
    handler: async (ctx) => {
      const text = ctx.argsText.trim();
      await ctx.sendText(text.length > 0 ? `${text} ¯\\_(ツ)_/¯` : "¯\\_(ツ)_/¯");
    },
  },
  {
    name: "invite",
    description: "Invite someone to this room",
    usage: "/invite @user:server",
    args: ["user"],
    handler: async (ctx) => {
      const target = ctx.argsText.trim();
      if (!INVITE_TARGET.test(target)) {
        ctx.showToast("Use /invite @user:server");
        recordSlash("invite", false);
        throw new Error("Invalid invite target");
      }
      try {
        await ctx.inviteUser(target);
      } catch (err) {
        console.error("invite failed", err);
        ctx.showToast(
          "Couldn't send the invitation. Check that you can invite people to this room and try again.",
        );
        recordSlash("invite", false);
        throw err;
      }
      ctx.showToast("Invitation sent.");
      recordSlash("invite", true);
    },
  },
];

for (const cmd of builtIns) register(cmd);
